import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Demonstrates how primitives create dynamic geometric constructions from function features.
 *
 * A draggable quadratic forms a triangle between its two roots and vertex.
 * All primitives update reactively when the handle moves the parabola:
 *
 * - **polygon**: Triangle from roots (x₀, 0), (x₁, 0) and vertex (h, k)
 * - **point**: Midpoint of the base, computed via expression `(f-roots.x0 + f-roots.x1) / 2`
 * - **line**: Median from midpoint to vertex
 * - **line**: Height line from vertex down to x-axis
 * - **label**: Base length and height values
 */
export const TEST_VERTEX_TRIANGLE_PRESET: GraphpadWidgetConfig = {
  id: 'test-vertex-triangle',
  type: 'graphpad',
  params: {
    // Draggable quadratic f(x) = (x-3)² - 4
    f: {
      type: 'quadratic-vertex',
      params: { a: 1, h: 3, k: -4 },
    },
    'f-handle': {
      type: 'handle',
      params: { of: 'f', behavior: 'translate' },
    },
    'f-roots': {
      type: 'roots',
      params: { of: 'f' },
    },
    'f-extrema': {
      type: 'extrema',
      params: { of: 'f' },
    },

    // --- Triangle: roots + vertex ---
    triangle: {
      type: 'polygon',
      params: {
        vertices: [
          { x: 'f-roots.x0', y: 0 },
          { x: 'f-roots.x1', y: 0 },
          { x: 'f.h', y: 'f.k' },
        ],
      },
    },

    // --- Midpoint of the base (between the two roots) ---
    'base-midpoint': {
      type: 'point',
      params: {
        x: '(f-roots.x0 + f-roots.x1) / 2',
        y: 0,
        name: 'M',
      },
    },

    // --- Median: midpoint → vertex ---
    median: {
      type: 'line',
      params: {
        from: { x: '(f-roots.x0 + f-roots.x1) / 2', y: 0 },
        to: { x: 'f.h', y: 'f.k' },
      },
    },

    // --- Height: vertex → x-axis (vertical drop) ---
    height: {
      type: 'line',
      params: {
        from: { x: 'f.h', y: 'f.k' },
        to: { x: 'f.h', y: 0 },
      },
    },

    // --- Label: base length ---
    'label-base': {
      type: 'label',
      params: {
        text: 'base = {f-roots.x1} − {f-roots.x0}',
        precision: 1,
        position: { x: '(f-roots.x0 + f-roots.x1) / 2', y: -1 },
      },
    },

    // --- Label: height ---
    'label-height': {
      type: 'label',
      params: {
        text: 'height = {f.k}',
        precision: 1,
        position: { x: 'f.h', y: 'f.k / 2' },
      },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    math: { defaultPrecision: 2 },
    coordinateSystem: {
      boundingbox: { xStart: -4, xEnd: 12, yStart: -8, yEnd: 6 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      f: {
        curve: { color: 'sky', strokeWidth: 3 },
      },
      'f-handle': {
        point: { color: 'sky', size: 8 },
      },
      'f-roots': {
        point: {
          color: 'green',
          label: { text: '({x}, 0)', precision: 1, position: 'bottom' },
        },
      },
      'f-extrema': {
        subparts: {
          vertex: {
            point: {
              color: 'red',
              label: { text: '({x}, {y})', precision: 1, position: 'topRight' },
            },
          },
        },
      },

      // Triangle fill
      triangle: {
        polygon: { fillColor: 'indigo', fillOpacity: 0.1, strokeColor: 'indigo', strokeWidth: 1 },
      },

      // Midpoint
      'base-midpoint': {
        point: { color: 'orange' },
      },

      // Median (dashed)
      median: {
        line: { color: 'orange', strokeWidth: 1, dash: 3 },
      },

      // Height (dashed)
      height: {
        line: { color: 'red', strokeWidth: 1, dash: 2 },
      },

      // Labels
      'label-base': {
        label: { variant: 'secondary', color: 'indigo' },
      },
      'label-height': {
        label: { variant: 'secondary', color: 'red', offset: [15, 0] },
      },
    },
  },
};
